import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  showValue?: boolean;
  formatValue?: (value: number) => string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      className,
      value,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      label,
      showValue = true,
      formatValue = (v) => String(v),
      ...props
    },
    ref
  ) => {
    // Calculate percentage for the gradient fill. Clamped so an out-of-range
    // value (e.g. a restored draft above the current max) can't paint past the
    // ends of the track.
    const percentage = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);

    return (
      <div className={cn('w-full', className)}>
        {(label || showValue) && (
          <div className="flex items-center justify-between mb-2">
            {label && (
              <span className="text-sm font-medium text-text">{label}</span>
            )}
            {showValue && (
              <span className="text-sm font-semibold text-coral">
                {formatValue(value)}
              </span>
            )}
          </div>
        )}
        {/* The input is a full 44px tall (the minimum touch target) but paints
            nothing itself — the visible 8px track is a separate, non-interactive
            layer behind it. Sizing the input to the track instead, as this used
            to, left an 8px-tall hit area that was near-impossible to drag on a
            phone: most touches missed and scrolled the page instead. */}
        <div className="relative flex items-center h-11">
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 rounded-full pointer-events-none"
            style={{
              background: `linear-gradient(to right, #FF6B6B 0%, #845EF7 ${percentage}%, var(--color-border) ${percentage}%, var(--color-border) 100%)`,
            }}
          />
          <input
            ref={ref}
            type="range"
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            min={min}
            max={max}
            step={step}
            className={cn(
              'relative w-full h-11 bg-transparent appearance-none cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30 focus-visible:rounded-xl',
              // Transparent full-height tracks so the whole 44px band is
              // draggable/tappable while the visual track stays 8px.
              '[&::-webkit-slider-runnable-track]:h-11',
              '[&::-webkit-slider-runnable-track]:bg-transparent',
              '[&::-moz-range-track]:h-11',
              '[&::-moz-range-track]:bg-transparent',
              '[&::-webkit-slider-thumb]:appearance-none',
              '[&::-webkit-slider-thumb]:w-5',
              '[&::-webkit-slider-thumb]:h-5',
              // Centres the 20px thumb in the 44px track: (44 - 20) / 2.
              '[&::-webkit-slider-thumb]:mt-3',
              '[&::-webkit-slider-thumb]:rounded-full',
              '[&::-webkit-slider-thumb]:bg-white',
              '[&::-webkit-slider-thumb]:shadow-md',
              '[&::-webkit-slider-thumb]:border-2',
              '[&::-webkit-slider-thumb]:border-coral',
              '[&::-webkit-slider-thumb]:cursor-pointer',
              '[&::-webkit-slider-thumb]:transition-transform',
              '[&::-webkit-slider-thumb]:hover:scale-110',
              '[&::-moz-range-thumb]:w-5',
              '[&::-moz-range-thumb]:h-5',
              '[&::-moz-range-thumb]:box-border',
              '[&::-moz-range-thumb]:rounded-full',
              '[&::-moz-range-thumb]:bg-white',
              '[&::-moz-range-thumb]:shadow-md',
              '[&::-moz-range-thumb]:border-2',
              '[&::-moz-range-thumb]:border-coral',
              '[&::-moz-range-thumb]:cursor-pointer'
            )}
            {...props}
          />
        </div>
      </div>
    );
  }
);

Slider.displayName = 'Slider';
